import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brush, Shirt, Gem, Scissors } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import AdSlot from "@/components/AdSlot";

const FAMILIES = [
  { key: "spring", it: "Primavera", en: "Spring", color: "#F4B183" },
  { key: "summer", it: "Estate", en: "Summer", color: "#A9C0D6" },
  { key: "autumn", it: "Autunno", en: "Autumn", color: "#B5651D" },
  { key: "winter", it: "Inverno", en: "Winter", color: "#3E5A8E" },
];

export default function Armocromia() {
  const { lang, L } = useI18n();
  const [seasons, setSeasons] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/seasons").then(({ data }) => setSeasons(data)); }, []);

  return (
    <div>
      <div className="rose-gold-bg text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-white/80">Armocromia professionale</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl">Le 12 stagioni armocromatiche</h1>
          <p className="mt-4 max-w-2xl text-white/90 text-lg">Ogni stagione ha la sua palette, i colori da evitare e i consigli su trucco, abbigliamento, gioielli e capelli. Scopri la tua con il quiz.</p>
          <Link to="/quiz" data-testid="armo-quiz-cta" className="mt-6 inline-flex rounded-full bg-charcoal text-white font-semibold px-7 py-3.5 hover:-translate-y-0.5 transition-transform">Scopri la tua stagione</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
        {FAMILIES.map((fam) => (
          <div key={fam.key} className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-4 h-4 rounded-full" style={{ background: fam.color }} />
              <h2 className="font-serif text-3xl">{lang === "it" ? fam.it : fam.en}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {seasons.filter((s) => s.family === fam.key).map((s, i) => (
                <motion.div key={s.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-rose-100 overflow-hidden hover-lift cursor-pointer" onClick={() => setOpen(open === s.key ? null : s.key)} data-testid={`season-${s.key}`}>
                  <div className="grid grid-cols-8 h-16">
                    {s.palette.map((c, j) => <div key={j} style={{ background: c }} />)}
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl">{L(s, "name")}</h3>
                    <p className="mt-1 text-sm text-charcoal/55 leading-relaxed">{L(s, "desc")}</p>
                    {open === s.key && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 space-y-3 text-sm">
                        <div className="flex gap-2"><Brush size={16} className="text-rose-500 shrink-0 mt-0.5" /><span>{L(s.makeup, "")}</span></div>
                        <div className="flex gap-2"><Shirt size={16} className="text-rose-500 shrink-0 mt-0.5" /><span>{L(s.clothes, "")}</span></div>
                        <div className="flex gap-2"><Gem size={16} className="text-rose-500 shrink-0 mt-0.5" /><span>{L(s.jewelry, "")}</span></div>
                        <div className="flex gap-2"><Scissors size={16} className="text-rose-500 shrink-0 mt-0.5" /><span>{L(s.hair, "")}</span></div>
                      </motion.div>
                    )}
                    <p className="mt-3 text-xs font-semibold text-rose-500">{open === s.key ? "Chiudi" : "Vedi i dettagli →"}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
        <AdSlot format="leaderboard" />
      </div>
    </div>
  );
}
